import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SendIcon from '@mui/icons-material/Send';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import clientInboxApi from '@src/api/clientInbox';
import ComposerReport from '@src/components/client-inbox/ComposerReport';
import ThreadAgentPanel from '@src/components/client-inbox/ThreadAgentPanel';
import UnrunActionDialog, { findUnrunStaffAction } from '@src/components/client-inbox/UnrunActionDialog';
import {
  AutosendBanner,
  ComposerStatusChips,
  InvestigationBanner,
  StaffInstructionHistory,
} from '@src/components/client-inbox/ThreadAutomationStatus';
import { createTask, listAssignees } from '@src/api/tasks';
import { TextEditor } from '@src/components/core/text-editor/text-editor';
import ThreadMessage from './ThreadMessage';
import { buildReplyRecipients, parseAddressInput } from './emailHelpers';

dayjs.extend(relativeTime);

const staffUserLabel = (user) => user?.full_name || user?.email || 'Unknown';

// Extract the inbox thread id from a task source key of the shape
// `inbox-thread:<id>` or `inbox-thread:<id>:step-N`.
export function threadIdForTask(task) {
  const key = task?.source_key || '';
  if (!key.startsWith('inbox-thread:')) return null;
  const raw = key.split(':')[1];
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Inline email panel for inbox-sourced tasks on the /tasks page: shows the
 * real conversation (same ThreadMessage renderer as the Client Inbox page)
 * plus a full reply composer (From/To/CC/Subject/body/attachments) that sends
 * through the same reply endpoint. Sending the reply closes the task on the
 * backend, so `onSent` is called afterwards to refresh the table row.
 */
function TaskEmailPanel({ task, threadId, onSent, onError, onBodyChange }) {
  const resolvedThreadId = threadId || threadIdForTask(task);

  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [senderOptions, setSenderOptions] = useState([]);
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [ccValue, setCcValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');
  const [bodyValue, setBodyValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [contextNote, setContextNote] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  const [listening, setListening] = useState(false);
  const [interimVoiceText, setInterimVoiceText] = useState('');
  const [polishing, setPolishing] = useState(false);
  const [composerReport, setComposerReport] = useState(null);
  const [keepWording, setKeepWording] = useState(false);
  const [showContextNote, setShowContextNote] = useState(false);
  // Internal notes (team collaboration — never client-visible).
  const [notes, setNotes] = useState([]);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notifyIds, setNotifyIds] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  // "Request input" — delegate a question to a teammate as a child task.
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestUserId, setRequestUserId] = useState('');
  const [requestText, setRequestText] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  // Draft autosave — edits persist to thread.reply_draft (same store the
  // inbox page uses) so collapsing the row / switching tasks loses nothing.
  const [draftSaved, setDraftSaved] = useState(false);
  const draftSaveTimerRef = useRef(null);
  const lastSavedDraftRef = useRef('');
  const latestBodyRef = useRef('');
  // Undo window: seconds remaining before the queued payload actually sends.
  const [pendingSeconds, setPendingSeconds] = useState(null);
  const pendingPayloadRef = useRef(null);
  const recognitionRef = useRef(null);
  const bodyEditorRef = useRef(null);
  // Tracks which (thread, task revision) the body was last initialized for so
  // it re-initializes when task.updated_at changes (e.g. the proposal was
  // regenerated elsewhere) but is NOT clobbered when the thread object merely
  // refreshes after a regenerate-draft call here.
  const bodyInitKeyRef = useRef('');
  // The thread this panel shows right now; async results for any other
  // thread are dropped (Focus Mode reuses one panel across tasks).
  const threadIdRef = useRef(resolvedThreadId);
  threadIdRef.current = resolvedThreadId;
  const agentPanelRef = useRef(null);
  const [cancellingAutosend, setCancellingAutosend] = useState(false);
  const [cancelledAutosendId, setCancelledAutosendId] = useState(null);
  const [unrunSendAction, setUnrunSendAction] = useState(null);
  const [runningUnrunAction, setRunningUnrunAction] = useState(false);

  const reportError = useCallback(
    (msg) => {
      if (onError) onError(msg);
    },
    [onError],
  );

  // Load available sender addresses (same shape as the inbox page:
  // { senders: [...], default: '...' }).
  useEffect(() => {
    let active = true;
    clientInboxApi
      .listEmailSenders()
      .then((response) => {
        if (!active) return;
        const senders = response?.data?.senders || [];
        const resolvedDefault = response?.data?.default || '';
        // Always offer the resolved per-user default even if the configured
        // sender list is empty/missing it — never leave From blank.
        const merged = resolvedDefault && !senders.includes(resolvedDefault)
          ? [resolvedDefault, ...senders]
          : senders;
        setSenderOptions(merged);
        setFromValue((current) => {
          if (current && merged.includes(current)) return current;
          return resolvedDefault || merged[0] || '';
        });
      })
      .catch((err) => {
        console.error('Failed to load available senders', err);
        if (active) reportError('Unable to load available sender addresses.');
      });
    return () => {
      active = false;
    };
  }, [reportError]);

  // Load the full thread on mount and whenever the thread id changes.
  useEffect(() => {
    if (!resolvedThreadId) return undefined;
    let active = true;
    setThread(null);
    setLoading(true);
    setLoadError('');
    clientInboxApi
      .retrieveEmailMessage(resolvedThreadId)
      .then((response) => {
        if (!active) return;
        setThread(response.data || null);
      })
      .catch((err) => {
        console.error('Failed to load email thread for task', err);
        if (!active) return;
        setLoadError('Unable to load the email conversation for this task.');
        reportError('Unable to load the email conversation for this task.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedThreadId, reportError]);

  // Load internal notes for the thread.
  useEffect(() => {
    if (!resolvedThreadId) return undefined;
    let active = true;
    setNotes([]);
    clientInboxApi
      .listThreadNotes(resolvedThreadId)
      .then((response) => {
        if (!active) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setNotes(data);
      })
      .catch((err) => {
        console.error('Failed to load internal notes', err);
      });
    return () => {
      active = false;
    };
  }, [resolvedThreadId]);

  // Staff users for the notify select + Request-input teammate select — the
  // same assignees endpoint the tasks page filters use.
  useEffect(() => {
    let active = true;
    listAssignees()
      .then((response) => {
        if (!active) return;
        const data = response.data?.results || response.data || [];
        setStaffUsers(
          data
            .map((user) => ({ id: user.id, full_name: user.full_name, email: user.email }))
            .sort((a, b) => staffUserLabel(a).localeCompare(staffUserLabel(b))),
        );
      })
      .catch((err) => {
        console.error('Failed to load staff users for notes', err);
      });
    return () => {
      active = false;
    };
  }, []);

  // Prefill the composer once the thread is loaded (subject + body). The body
  // re-initializes when the task revision changes (task.updated_at).
  useEffect(() => {
    if (!thread) return;
    setSubjectValue((current) => {
      if (current) return current;
      const base = thread.subject || 'Client email';
      return /^re:/i.test(base) ? base : `Re: ${base}`;
    });
    const initKey = `${resolvedThreadId}:${task.updated_at || ''}`;
    if (bodyInitKeyRef.current === initKey) return;
    bodyInitKeyRef.current = initKey;
    // Working draft (thread.reply_draft — kept fresh by autosave + regenerate)
    // wins over the AI's original proposal, so click-away never loses edits.
    // ai_proposed_reply stays untouched as the calibration baseline.
    const nextBody = thread.reply_draft || task.ai_proposed_reply || '';
    lastSavedDraftRef.current = nextBody;
    // Also reset the autosave ref: in Focus Mode this panel instance survives
    // task switches, and a stale latestBodyRef would flush the PREVIOUS
    // thread's HTML into this thread's reply_draft on the next switch-away.
    latestBodyRef.current = nextBody;
    setBodyValue(nextBody);
    if (onBodyChange) onBodyChange(nextBody);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread, resolvedThreadId, task.updated_at, task.ai_proposed_reply]);

  // Recipients depend on the chosen sender (auto-CC of the internal partner),
  // mirroring the inbox page's recompute-on-sender-change behavior.
  useEffect(() => {
    if (!thread) return;
    const recipients = buildReplyRecipients(thread, fromValue);
    setToValue(recipients.to);
    setCcValue(recipients.cc);
  }, [thread, fromValue]);

  // Earliest → latest so the conversation reads top-down; the latest message
  // starts expanded, older ones collapsed.
  const sortedMessages = useMemo(() => {
    const list = Array.isArray(thread?.messages) ? thread.messages.slice() : [];
    return list.sort((a, b) => {
      const ta = new Date(a.received_at || a.created_at).getTime();
      const tb = new Date(b.received_at || b.created_at).getTime();
      if (ta !== tb) return ta - tb;
      return (a.id || 0) - (b.id || 0);
    });
  }, [thread]);

  // Backend returns notes newest-first; display oldest → newest so the
  // discussion reads top-down like the conversation above it.
  const sortedNotes = useMemo(() => {
    const list = notes.slice();
    return list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [notes]);

  const handleAddNote = () => {
    const body = noteText.trim();
    if (!resolvedThreadId || !body || addingNote) return;
    setAddingNote(true);
    clientInboxApi
      .createThreadNote(resolvedThreadId, { body, notify_user_ids: notifyIds })
      .then((response) => {
        if (response?.data) setNotes((prev) => [response.data, ...prev]);
        setNoteText('');
        setNotifyIds([]);
      })
      .catch((err) => {
        console.error('Failed to add internal note', err);
        reportError('Unable to add the internal note.');
      })
      .finally(() => setAddingNote(false));
  };

  const handleRequestInput = () => {
    const question = requestText.trim();
    if (!resolvedThreadId || !question || !requestUserId || requestSubmitting) return;
    const teammate = staffUsers.find((user) => user.id === requestUserId);
    const teammateName = staffUserLabel(teammate);
    setRequestSubmitting(true);
    createTask({
      client: task.client,
      parent: task.id,
      title: `Input needed: ${question.length > 60 ? `${question.slice(0, 60)}…` : question}`,
      description: question,
      priority: 'medium',
      status: 'backlog',
      assignee: requestUserId,
    })
      .then(() =>
        // Record the delegation on the thread and email the teammate.
        clientInboxApi.createThreadNote(resolvedThreadId, {
          body: `Requested input from ${teammateName}: ${question}`,
          notify_user_ids: [requestUserId],
        }),
      )
      .then((response) => {
        if (response?.data) setNotes((prev) => [response.data, ...prev]);
        setRequestOpen(false);
        setRequestText('');
        setRequestUserId('');
        setNotesExpanded(true);
      })
      .catch((err) => {
        console.error('Failed to request input from teammate', err);
        reportError('Unable to create the input-request task.');
      })
      .finally(() => setRequestSubmitting(false));
  };

  const handleRegenerate = () => {
    if (!resolvedThreadId || regenerating) return;
    setRegenerating(true);
    setSendError('');
    const payload = {};
    if (contextNote.trim()) {
      payload.additional_context = contextNote.trim();
    }
    clientInboxApi
      .regenerateEmailThreadDraft(resolvedThreadId, payload)
      .then((response) => {
        const updatedThread = response.data?.thread;
        if (updatedThread) {
          setThread(updatedThread);
          setBodyValue(updatedThread.reply_draft || '');
          if (onBodyChange) onBodyChange(updatedThread.reply_draft || '');
          // Server already persisted the regenerated draft — sync the
          // autosave baseline so we don't immediately re-save it.
          lastSavedDraftRef.current = updatedThread.reply_draft || '';
          latestBodyRef.current = updatedThread.reply_draft || '';
        }
        setContextNote('');
      })
      .catch((err) => {
        console.error('Failed to regenerate reply draft', err);
        setSendError('Unable to regenerate the AI draft for this thread.');
        reportError('Unable to regenerate the AI draft for this thread.');
      })
      .finally(() => setRegenerating(false));
  };

  // "Ask the drafter" rewrote the draft server-side: reload the thread and
  // put the new draft in the composer.
  const handleAgentDraftUpdated = useCallback(
    (tid) => {
      if (!tid || threadIdRef.current !== tid) return;
      clientInboxApi
        .retrieveEmailMessage(tid)
        .then((response) => {
          const fresh = response.data;
          if (threadIdRef.current !== tid || !fresh) return;
          setThread(fresh);
          const draft = fresh.reply_draft || '';
          setBodyValue(draft);
          if (onBodyChange) onBodyChange(draft);
          lastSavedDraftRef.current = draft;
          latestBodyRef.current = draft;
        })
        .catch(() => {});
    },
    [onBodyChange],
  );

  const handleAgentUseDraft = (tid, html) => {
    if (!tid || threadIdRef.current !== tid) return;
    const draft = html || '';
    setBodyValue(draft);
    if (onBodyChange) onBodyChange(draft);
    scheduleDraftSave(draft);
  };

  const handleCancelAutosend = (srId) => {
    const tid = resolvedThreadId;
    if (!tid || !srId) return;
    setCancellingAutosend(true);
    clientInboxApi
      .cancelScheduledReply(tid, srId)
      .then(() => {
        if (threadIdRef.current === tid) setCancelledAutosendId(srId);
      })
      .catch(() => {
        if (threadIdRef.current === tid) reportError('Failed to cancel the auto-send.');
      })
      .finally(() => setCancellingAutosend(false));
  };

  useEffect(() => {
    setUnrunSendAction(null);
  }, [resolvedThreadId]);

  // "Run it" from the not-run-yet prompt: same run-action endpoint as the
  // inbox page's action card, then load the re-drafted reply.
  const runUnrunAction = (staffAction) => {
    const tid = resolvedThreadId;
    if (!tid || !staffAction) return;
    setRunningUnrunAction(true);
    clientInboxApi
      .runStaffAction(tid, {
        action_index: staffAction.index,
        type: staffAction.type,
        overrides: staffAction.allowed_overrides?.includes('role') ? { role: 'admin' } : {},
        // About to send and no poll here: wait for the re-drafted reply.
        redraft_inline: true,
      })
      .then((response) => {
        if (threadIdRef.current !== tid) return;
        setUnrunSendAction(null);
        const updatedThread = response.data?.thread;
        if (updatedThread) {
          setThread(updatedThread);
          const draft = updatedThread.reply_draft || '';
          setBodyValue(draft);
          if (onBodyChange) onBodyChange(draft);
          lastSavedDraftRef.current = draft;
          latestBodyRef.current = draft;
        }
      })
      .catch((err) => {
        if (threadIdRef.current !== tid) return;
        setUnrunSendAction(null);
        setSendError(err?.response?.data?.detail || 'Could not complete that action.');
      })
      .finally(() => setRunningUnrunAction(false));
  };

  const handleSend = (force = false) => {
    if (!resolvedThreadId || sending || sent) return;
    if (pendingSeconds !== null) {
      // Already queued — a second ⌘+Enter means "send now".
      setPendingSeconds(0);
      return;
    }
    // The draft reads as if suggested actions are done; stop the send while
    // an executable one hasn't been run.
    if (force !== true) {
      const pending = findUnrunStaffAction(thread);
      if (pending) {
        setUnrunSendAction(pending);
        return;
      }
    }
    if (!fromValue) {
      setSendError('Select a sender before sending.');
      return;
    }
    const trimmedBody = (bodyValue || '').trim();
    if (!trimmedBody) {
      setSendError('Reply body cannot be empty.');
      return;
    }
    // Exact payload shape the Client Inbox page sends: multipart FormData when
    // attachments are present, plain JSON otherwise.
    let payload;
    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append('body', bodyValue);
      formData.append('subject', subjectValue);
      formData.append('from_email', fromValue);
      parseAddressInput(toValue).forEach((addr) => formData.append('to', addr));
      parseAddressInput(ccValue).forEach((addr) => formData.append('cc', addr));
      attachments.forEach((file) => formData.append('attachments', file));
      payload = formData;
    } else {
      payload = {
        body: bodyValue,
        subject: subjectValue,
        to: parseAddressInput(toValue),
        cc: parseAddressInput(ccValue),
        from_email: fromValue,
      };
    }
    // Queue with a 10-second undo window instead of sending immediately —
    // email is the one thing you can't un-send.
    pendingPayloadRef.current = payload;
    setSendError('');
    setPendingSeconds(10);
  };

  const performSend = useCallback(
    (payload) => {
      setSending(true);
      setSendError('');
      clientInboxApi
        .replyToEmailMessage(resolvedThreadId, payload)
        .then((response) => {
          setThread((current) => response.data || current);
          setAttachments([]);
          setSent(true);
          if (onSent) onSent();
        })
        .catch((err) => {
          console.error('Failed to send reply', err);
          setSendError('Unable to send the reply.');
          reportError('Unable to send the reply.');
        })
        .finally(() => setSending(false));
    },
    [resolvedThreadId, onSent, reportError],
  );

  // Debounced draft autosave → thread.reply_draft. Flushes on unmount so
  // switching tasks mid-edit still persists the latest text.
  const scheduleDraftSave = (html) => {
    latestBodyRef.current = html;
    setDraftSaved(false);
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      const value = latestBodyRef.current;
      if (!resolvedThreadId || value === lastSavedDraftRef.current) return;
      clientInboxApi
        .updateEmailMessage(resolvedThreadId, { reply_draft: value })
        .then(() => {
          lastSavedDraftRef.current = value;
          setDraftSaved(true);
        })
        .catch((err) => console.error('Failed to autosave draft', err));
    }, 1200);
  };

  useEffect(() => {
    // Thread switched (Focus Mode reuses one panel instance): neutralize the
    // autosave refs NOW — the new thread's data may not have loaded yet, and
    // until init runs a fast close would otherwise flush the previous
    // thread's text against THIS thread's id.
    latestBodyRef.current = '';
    lastSavedDraftRef.current = '';
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
      const value = latestBodyRef.current;
      if (resolvedThreadId && value && value !== lastSavedDraftRef.current) {
        clientInboxApi
          .updateEmailMessage(resolvedThreadId, { reply_draft: value })
          .catch((err) => console.error('Failed to flush draft on unmount', err));
      }
    };
  }, [resolvedThreadId]);

  // Countdown driver for the undo window.
  useEffect(() => {
    if (pendingSeconds === null) return undefined;
    if (pendingSeconds <= 0) {
      const payload = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      setPendingSeconds(null);
      if (payload) performSend(payload);
      return undefined;
    }
    const timer = setTimeout(
      () => setPendingSeconds((s) => (s === null ? null : s - 1)),
      1000,
    );
    return () => clearTimeout(timer);
  }, [pendingSeconds, performSend]);

  const cancelPendingSend = () => {
    pendingPayloadRef.current = null;
    setPendingSeconds(null);
  };

  const sendNow = () => setPendingSeconds(0);

  // Unmount safety: if the panel disappears mid-countdown (row collapsed,
  // page navigation within the SPA), the send still goes out — the user
  // asked to send; only an explicit Undo cancels.
  useEffect(
    () => () => {
      const payload = pendingPayloadRef.current;
      if (payload && resolvedThreadId) {
        pendingPayloadRef.current = null;
        clientInboxApi.replyToEmailMessage(resolvedThreadId, payload).catch((err) => {
          console.error('Failed to send queued reply on unmount', err);
        });
      }
    },
    [resolvedThreadId],
  );

  // Voice dictation — same pattern as the Client Inbox composer: final
  // transcripts append through the editor ref so the cursor stays put.
  const toggleVoiceInput = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSendError('Speech recognition is not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognitionRef.current = recognition;
    setInterimVoiceText('');

    const appendFinal = (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (bodyEditorRef.current) {
        bodyEditorRef.current.appendHtml(`<p>${trimmed}</p>`);
        return;
      }
      setBodyValue((current) => {
        const next = (current || '') + `<p>${trimmed}</p>`;
        if (onBodyChange) onBodyChange(next);
        return next;
      });
    };

    recognition.onresult = (event) => {
      let finalPart = '';
      let interimPart = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalPart += t;
        } else {
          interimPart += t;
        }
      }
      if (finalPart) appendFinal(finalPart);
      setInterimVoiceText(interimPart.trim());
    };
    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setListening(false);
      setInterimVoiceText('');
    };
    recognition.onend = () => {
      setListening(false);
      setInterimVoiceText('');
    };
    recognition.start();
    setListening(true);
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  // Polish rough/dictated notes into a formatted reply with full client
  // context — the same polish-draft endpoint the inbox page uses.
  const handlePolish = () => {
    if (!resolvedThreadId || polishing) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = bodyValue || '';
    const rawText = (tmp.textContent || tmp.innerText || '').trim();
    if (!rawText) return;
    setPolishing(true);
    setSendError('');
    setComposerReport(null);
    clientInboxApi
      .polishDraft(resolvedThreadId, { raw_text: rawText, honor_wording: keepWording })
      .then((res) => {
        const data = res?.data || {};
        const polished = data.polished_html || '';
        if (polished) {
          setBodyValue(polished);
          setComposerReport(data);
          if (onBodyChange) onBodyChange(polished);
        }
      })
      .catch((err) => {
        console.error('Failed to polish draft', err);
        setSendError('Unable to polish the draft with AI.');
        reportError('Unable to polish the draft with AI.');
      })
      .finally(() => setPolishing(false));
  };

  const handleBodyKeyDown = (event) => {
    // Cmd+Enter (mac) or Ctrl+Enter (win/linux) sends
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSend();
    }
  };

  if (!resolvedThreadId) {
    return null;
  }

  return (
    <Box>
      {loading ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
      ) : (
        <Box sx={{ mb: 2 }}>
          {/* Each message card carries its own sender + time — no need to
              repeat the subject/from metadata the task header already shows. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              Conversation
            </Typography>
            {thread?.messages?.length > 1 ? (
              <Chip size="small" variant="outlined" label={`${thread.messages.length} messages`} />
            ) : null}
          </Stack>
          {sortedMessages.length ? (
            sortedMessages.map((msg, idx) => (
              <ThreadMessage
                key={msg.id}
                msg={msg}
                defaultExpanded={idx === sortedMessages.length - 1}
                isInternal={msg.is_internal}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No messages found on this thread.
            </Typography>
          )}
        </Box>
      )}

      {!loading && !loadError && thread?.relevant_links?.length ? (
        // Same contextual quick links the inbox page shows — the AI's
        // category maps the question to the relevant app pages for this client.
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
            Quick links:
          </Typography>
          {thread.relevant_links.map((link) => (
            <Button
              key={link.url}
              size="small"
              variant="outlined"
              color="inherit"
              href={link.url}
              target="_blank"
              endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
              sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}
            >
              {link.label}
            </Button>
          ))}
        </Stack>
      ) : null}

      {!loading && !loadError ? (
        <Box
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: 1,
            bgcolor: 'warning.lighter',
            px: 1.5,
            py: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              size="small"
              color="inherit"
              onClick={() => setNotesExpanded((prev) => !prev)}
              startIcon={(
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: notesExpanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              )}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Internal notes ({notes.length})
            </Button>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Team-only — never sent to the client
              </Typography>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => setRequestOpen(true)}
                startIcon={<PersonAddAlt1Icon fontSize="small" />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Request input
              </Button>
            </Stack>
          </Stack>
          <Collapse in={notesExpanded}>
            <Stack spacing={1} sx={{ pb: 1.5, pt: 0.5 }}>
              {sortedNotes.length ? (
                sortedNotes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'warning.light',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      px: 1.25,
                      py: 0.75,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {note.author_name || note.author_email || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(note.created_at).fromNow()}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {note.body}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No internal notes yet.
                </Typography>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  label="Add an internal note"
                  value={noteText}
                  onChange={(event) => setNoteText(event.target.value)}
                  multiline
                  minRows={1}
                  fullWidth
                  sx={{ bgcolor: 'background.paper' }}
                />
                <TextField
                  select
                  size="small"
                  label="Notify"
                  value={notifyIds}
                  onChange={(event) => setNotifyIds(event.target.value)}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected
                        .map((id) => staffUserLabel(staffUsers.find((user) => user.id === id)))
                        .join(', '),
                  }}
                  sx={{ minWidth: 180, bgcolor: 'background.paper' }}
                >
                  {staffUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {staffUserLabel(user)}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={handleAddNote}
                  disabled={addingNote || !noteText.trim()}
                  sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                >
                  {addingNote ? 'Adding…' : 'Add'}
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Box>
      ) : null}

      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request input from a teammate</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Creates a child task assigned to them and posts an internal note on this
              thread — they get an email with your question.
            </Typography>
            <TextField
              select
              size="small"
              label="Teammate"
              value={requestUserId}
              onChange={(event) => setRequestUserId(event.target.value)}
              fullWidth
            >
              {staffUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {staffUserLabel(user)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="What do you need?"
              value={requestText}
              onChange={(event) => setRequestText(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setRequestOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestInput}
            disabled={requestSubmitting || !requestUserId || !requestText.trim()}
            startIcon={requestSubmitting ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {requestSubmitting ? 'Sending…' : 'Request input'}
          </Button>
        </DialogActions>
      </Dialog>

      {sent ? (
        <Alert severity="success" sx={{ mb: 1 }}>
          Reply sent. The task will be closed automatically.
        </Alert>
      ) : null}
      {sendError ? (
        <Alert severity="error" sx={{ mb: 1 }}>{sendError}</Alert>
      ) : null}

      {!loading && !loadError && !sent ? (
        <Stack spacing={1.25}>
          <AutosendBanner
            payload={
              thread?.analysis_payload?.autosend?.scheduled_reply_id === cancelledAutosendId
                ? null
                : thread?.analysis_payload
            }
            cancelling={cancellingAutosend}
            onCancel={handleCancelAutosend}
          />
          <InvestigationBanner
            payload={thread?.analysis_payload}
            onInvestigate={() => agentPanelRef.current?.investigate()}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                Reply
              </Typography>
              <ComposerStatusChips payload={thread?.analysis_payload} />
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant={listening ? 'contained' : 'text'}
                color={listening ? 'error' : 'inherit'}
                onClick={toggleVoiceInput}
                startIcon={listening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
              >
                {listening ? 'Stop' : 'Voice'}
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={handlePolish}
                disabled={polishing || !(bodyValue || '').trim()}
                startIcon={polishing ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
              >
                {polishing ? 'Composing…' : 'Polish with AI'}
              </Button>
              <Tooltip title="Polish as usual, but nothing you said gets contradicted, softened, dropped, or followed by a policy redirect. You can also just say 'keep my wording' or 'don't override me' in your notes.">
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={(
                    <Switch
                      size="small"
                      checked={keepWording}
                      onChange={(event) => setKeepWording(event.target.checked)}
                    />
                  )}
                  label={<Typography variant="caption">My message stands</Typography>}
                />
              </Tooltip>
            </Stack>
          </Stack>
          <ComposerReport report={composerReport} onDismiss={() => setComposerReport(null)} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="From"
              value={fromValue}
              onChange={(event) => setFromValue(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {senderOptions.map((sender) => (
                <MenuItem key={sender} value={sender}>
                  {sender}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="To"
              value={toValue}
              onChange={(event) => setToValue(event.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="CC"
              value={ccValue}
              onChange={(event) => setCcValue(event.target.value)}
              fullWidth
            />
          </Stack>
          <TextField
            size="small"
            label="Subject"
            value={subjectValue}
            onChange={(event) => setSubjectValue(event.target.value)}
            fullWidth
          />
          <Box
            onKeyDown={handleBodyKeyDown}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              minHeight: 140,
              backgroundColor: 'background.paper',
              opacity: regenerating ? 0.6 : 1,
              pointerEvents: regenerating ? 'none' : 'auto',
              '& .ProseMirror': { minHeight: 100, outline: 'none' },
              '& .ProseMirror p': { margin: '0 0 0.5em 0' },
              '& .ProseMirror p:last-child': { marginBottom: 0 },
              '& .is-editor-empty:first-of-type::before': {
                content: 'attr(data-placeholder)',
                color: 'text.disabled',
                float: 'left',
                height: 0,
                pointerEvents: 'none',
              },
            }}
          >
            <TextEditor
              key={`task-email-body-${task.id}-${resolvedThreadId}`}
              ref={bodyEditorRef}
              content={bodyValue || ''}
              onUpdate={({ editor }) => {
                const html = editor.getHTML();
                setBodyValue(html);
                // Keep the parent proposal card's draft state in step so the
                // Approve & Execute path sends what the user sees here.
                if (onBodyChange) onBodyChange(html);
                scheduleDraftSave(html);
              }}
              placeholder="Draft reply — type, dictate, or regenerate. Press ⌘+Enter to send."
            />
          </Box>
          {listening ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Listening… {interimVoiceText}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" variant="text" component="label">
              Attach files
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
                  e.target.value = '';
                }}
              />
            </Button>
            {attachments.map((file, idx) => (
              <Chip
                key={`${file.name}-${idx}`}
                label={file.name}
                size="small"
                onDelete={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={handleRegenerate}
              disabled={regenerating}
              startIcon={regenerating ? <CircularProgress size={14} /> : <AutorenewIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {regenerating ? 'Regenerating…' : 'Regenerate draft'}
            </Button>
            {!showContextNote ? (
              <Button size="small" variant="text" color="inherit" onClick={() => setShowContextNote(true)}>
                + add context
              </Button>
            ) : null}
          </Stack>
          {showContextNote ? (
            <TextField
              size="small"
              label="Context for regenerate (optional)"
              value={contextNote}
              onChange={(event) => setContextNote(event.target.value)}
              autoFocus
              fullWidth
            />
          ) : null}
          <StaffInstructionHistory key={resolvedThreadId} payload={thread?.analysis_payload} />
          <Stack direction="row" spacing={1} alignItems="center">
            {pendingSeconds !== null ? (
              <>
                <Button variant="contained" color="warning" onClick={cancelPendingSend}>
                  Undo
                </Button>
                <Button variant="text" onClick={sendNow}>
                  Send now
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Sending in {pendingSeconds}s…
                </Typography>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleSend()}
                  disabled={sending}
                  startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                >
                  {sending ? 'Sending…' : 'Send reply'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  ⌘+Enter (or Ctrl+Enter) to send — 10s undo window. Sending closes this task.
                  {draftSaved ? ' · Draft saved' : ''}
                </Typography>
              </>
            )}
          </Stack>
          <UnrunActionDialog
            staffAction={unrunSendAction}
            running={runningUnrunAction}
            onClose={() => setUnrunSendAction(null)}
            onSendAnyway={() => {
              setUnrunSendAction(null);
              handleSend(true);
            }}
            onRun={runUnrunAction}
          />
          <ThreadAgentPanel
            ref={agentPanelRef}
            threadId={resolvedThreadId}
            onDraftUpdated={handleAgentDraftUpdated}
            onUseDraft={handleAgentUseDraft}
          />
        </Stack>
      ) : null}
    </Box>
  );
}

TaskEmailPanel.propTypes = {
  task: PropTypes.object.isRequired,
  threadId: PropTypes.number,
  onSent: PropTypes.func,
  onError: PropTypes.func,
  onBodyChange: PropTypes.func,
};

TaskEmailPanel.defaultProps = {
  threadId: null,
  onSent: null,
  onError: null,
  onBodyChange: null,
};

export default TaskEmailPanel;
